import { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Chip, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LinkIcon from "@mui/icons-material/Link";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import EqipRichTextEditor from "./EqipRichTextEditor";
import ieqipCoreServiceApi from "../apis/ieqipCoreServiceApi";

const NoteEditor = ({ note, onSave, onCancel }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [context, setContext] = useState(null);

  const tenantId = useSelector((state) => state.tenantId);
  const idToken = useSelector((state) => state.token);
  const userEmail = useSelector((state) => state.user?.email);

  useEffect(() => {
    if (note) {
      setTitle(note.title || "");
      setContent(note.content_md || "");
      setContext(note.context ? JSON.parse(note.context) : null);
    } else {
      // Capture current context for new note
      const capturedContext = {
        routePath: location.pathname,
        query: Object.fromEntries(new URLSearchParams(location.search)),
        displayText: `${location.pathname}${location.search ? ` (${Object.keys(Object.fromEntries(new URLSearchParams(location.search))).length} filters)` : ""}`,
        timestamp: new Date().toISOString(),
      };
      setContext(capturedContext);
    }
  }, [note, location]);

  const handleSave = async () => {
    if (!title.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      const requestHeader = {
        headers: {
          "x-eqip-tenantid": tenantId,
          Authorization: idToken,
        },
      };

      if (note?.id) {
        // Update existing note - use simplified payload as per API spec
        const updatePayload = {
          title: title.trim(),
          content_md: content,
          content_html: content, // Backend expects both content_md and content_html
          updated_by: userEmail,
        };
        await ieqipCoreServiceApi.put(`/api/annotations/${note.user_annotation_uid}`, updatePayload, requestHeader);
      } else {
        // Create new note - use full payload
        const createPayload = {
          title: title.trim(),
          content_md: content,
          content_html: content,
          context_url: JSON.stringify(context),
          email: userEmail,
          created_by: userEmail,
        };
        await ieqipCoreServiceApi.post(`/api/annotations`, createPayload, requestHeader);
      }

      onSave();
    } catch (error) {
      console.error("Failed to save note:", error);
      alert(error?.response?.data?.message || error.message || "Failed to save note");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!note?.user_annotation_uid) return;
    
    const confirmDelete = window.confirm("Are you sure you want to delete this note? This action cannot be undone.");
    if (!confirmDelete) return;

    try {
      setIsDeleting(true);
      const requestHeader = {
        headers: {
          "x-eqip-tenantid": tenantId,
          Authorization: idToken,
        },
      };

      await ieqipCoreServiceApi.delete(`/api/annotations/${note.user_annotation_uid}`, requestHeader);
      onSave(); // Refresh the list after deletion
    } catch (error) {
      console.error("Failed to delete note:", error);
      alert(error?.response?.data?.message || error.message || "Failed to delete note");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNavigateToContext = () => {
    if (!context?.routePath) return;

    // Build query string from context.query
    const queryParams = new URLSearchParams(context.query || {});
    const fullPath = `${context.routePath}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    // Validate it's an internal route (starts with /)
    if (context.routePath.startsWith("/")) {
      navigate(fullPath);
    } else {
      console.warn("Context route is not internal:", context.routePath);
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" p={2}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton aria-label="Back to list" onClick={onCancel} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">{note ? "Edit Note" : "New Note"}</Typography>
      </Box>

      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        size="small"
        sx={{ mb: 2 }}
        inputProps={{ "aria-label": "Note title" }}
      />

      {context && (
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Context:
          </Typography>
          <Chip
            icon={<LinkIcon />}
            label={context.displayText || context.routePath}
            size="small"
            onClick={handleNavigateToContext}
            clickable
            sx={{ 
              maxWidth: "100%",
              "& .MuiChip-label": {
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
            }}
            aria-label={`Navigate to ${context.displayText || context.routePath}`}
          />
        </Box>
      )}

      <Box mb={2} sx={{ flex: 1, minHeight: 200 }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          Content:
        </Typography>
        <EqipRichTextEditor
          value={content}
          onChange={setContent}
          placeholder="Start typing your note..."
        />
      </Box>

      <Box display="flex" gap={1} justifyContent="space-between">
        <Box>
          {note?.user_annotation_uid && (
            <Button 
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              aria-label="Delete note"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button onClick={onCancel} disabled={isSaving || isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || isDeleting || !title.trim()}
            aria-label="Save note"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NoteEditor;

